import { useQuery } from '@tanstack/react-query';
import { every, includes, some, startsWith } from 'es-toolkit/compat';
import { StringParam, useQueryParam } from 'use-query-params';

import { getUserLabPermissions } from 'api/userCredentials';
import { Permission, PermissionSubOptions } from 'interfaces/permissionOption';
import { useAppSelector } from 'redux/hooks';

export const usePermissions = () => {
  const { accessToken, defaultLabId: userLabId } = useAppSelector((state) => state.auth);
  const [queryLabId] = useQueryParam('labId', StringParam);
  const labId = queryLabId || userLabId;

  const {
    data: permissions,
    isLoading: isLoadingState,
    error,
    isError,
  } = useQuery(['permissions', labId], ({ signal }) => getUserLabPermissions(labId, signal), {
    enabled: Boolean(accessToken) && Boolean(labId),
  });

  const isLoading = isLoadingState && !isError;

  const hasPermission = (permission: Permission) => includes(permissions, permission);
  const hasSubPermission = <T extends keyof PermissionSubOptions>(
    permission: T,
    subPermission: PermissionSubOptions[T][number]
  ) => includes(permissions, `${permission}.${subPermission}`) || includes(permissions, `${permission}.*`);

  /**
   * Check if the user has any of the sub-permissions of a given permission
   */
  const hasSomeSubPermission = <T extends keyof PermissionSubOptions>(permission: T) => {
    const permissionWithDot = `${permission}.`;
    return some(permissions, (userPermission) => startsWith(userPermission, permissionWithDot));
  };

  const hasSomePermissions = (requiredPermissions: Permission[]) => some(requiredPermissions, hasPermission);
  const hasEveryPermission = (requiredPermissions: Permission[]) => every(requiredPermissions, hasPermission);

  return {
    hasPermission,
    hasSubPermission,
    hasSomeSubPermission,
    hasEveryPermission,
    hasSomePermissions,
    permissions,
    isLoading,
    isError,
    error,
  } as const;
};
